import { createApi } from '@reduxjs/toolkit/query/react';
import { z } from 'zod';
import { baseQuery } from './baseQuery';
import { validateResponse } from './validateResponse';

// ---------------------------------------------------------------------------
// Request schema — mirrors HcoSearch.Request C# class
// ---------------------------------------------------------------------------

export const HcoSearchV3RequestSchema = z.object({
    search: z.object({
        orgName:            z.string().optional(),
        orgId:              z.string().optional(),
        address:            z.string().optional(),
        city:               z.string().optional(),
        state:              z.string().optional(),
        zip:                z.string().optional(),
        universe:           z.string().optional(),
        orgType:            z.string().optional(),
        tin:                z.string().optional(),
        showAll:            z.string().optional(),
        npi:                z.string().optional(),
        inNetworkPharmacy:  z.string().optional(),
    }),
    user: z.object({
        team:      z.string().optional(),
        territory: z.string().optional(),
    }),
    pageNumber: z.number().int().min(1).default(1),
    pageSize:   z.number().int().min(1).max(100).default(20),
});

export type HcoSearchV3Request = z.infer<typeof HcoSearchV3RequestSchema>;

// ---------------------------------------------------------------------------
// Response row schema — mirrors HcoSearchRowV3 C# class (camelCase from JSON)
// ---------------------------------------------------------------------------

const ns = z.string().nullish();
const nn = z.number().nullish();
const nd = z.string().nullish(); // date as string from JSON

export const HcoSearchRowV3Schema = z.object({
    // Core org identity
    orgId:                      nn,
    orgIdNew:                   nn,
    parentOrgId:                nn,
    locId:                      nn,
    bldgId:                     nn,
    orgName:                    ns,
    ctmsName:                   ns,
    orgStatus:                  ns,
    orgType:                    ns,
    preferredName:              ns,
    // Address
    address1:                   ns,
    address2:                   ns,
    city:                       ns,
    state:                      ns,
    zip5:                       ns,
    addrValid:                  ns,
    addrType:                   ns,
    // Contact
    phone:                      ns,
    fax:                        ns,
    email:                      ns,
    website:                    ns,
    mainContact:                ns,
    noContact:                  ns,
    notes:                      ns,
    // Flags
    aggspendFlag:               ns,
    aggspendStatus:             ns,
    universeFlag:               ns,
    universeType:               ns,
    targetFlag:                 ns,
    targetType:                 ns,
    ctmsFlag:                   ns,
    researchSite:               ns,
    // Additional IDs
    orgIdDw:                    nn,
    nabpId:                     ns,
    outletId:                   ns,
    tin:                        ns,
    govtentType:                ns,
    campusId:                   nn,
    siteId:                     nn,
    systemId:                   nn,
    aggspendStatusModifyDate:   nd,
    // orgs_ext
    npi:                        ns,
    targetFlagTm21:             ns,
    targetTypeTm21:             ns,
    targetFlagTm22:             ns,
    targetTypeTm22:             ns,
    universeFlagTm11:           ns,
    universeFlagTm21:           ns,
    universeFlagTm22:           ns,
    universeFlagTm51:           ns,
    universeFlagTm52:           ns,
    universeFlagTm61:           ns,
    universeFlagTm13:           ns,
    universeFlagTm54:           ns,
    universeFlagTm64:           ns,
    // Territory
    terrId:                     ns,
    mdmOverride:                ns,
    mdmTerrId:                  ns,
    // Computed
    affiliationCount:           z.number().nullish(),
    inNetworkPharmacy:          ns,
});

export type HcoSearchRowV3 = z.infer<typeof HcoSearchRowV3Schema>;

// ---------------------------------------------------------------------------
// Paginated response envelope
// ---------------------------------------------------------------------------

export const HcoSearchV3ResponseSchema = z.object({
    rows:       HcoSearchRowV3Schema.array(),
    totalCount: z.number().int(),
});

export type HcoSearchV3Response = z.infer<typeof HcoSearchV3ResponseSchema>;

// ---------------------------------------------------------------------------
// RTK Query slice
// ---------------------------------------------------------------------------

export const organizationsV3Api = createApi({
    reducerPath: 'organizationsV3Api',
    baseQuery,
    endpoints: (builder) => ({
        searchOrganizationsV3: builder.mutation<HcoSearchV3Response, HcoSearchV3Request>({
            query: (body) => ({
                url:    'api/v3/organizations/search',
                method: 'POST',
                body,
            }),
            transformResponse: validateResponse(HcoSearchV3ResponseSchema),
        }),
    }),
});

export const { useSearchOrganizationsV3Mutation } = organizationsV3Api;
